import { match, type StrictExtract, type StrictOmit } from '@frontend/duck-tape';
import type { LinkButtonProps } from '@frontend/web-react';
import { Icon, Spinner, Text, type IconProps, type TextProps } from '@frontend/web-react';
import type { IconName } from '@frontend/web-react/components/Primitives/Icon/helpers';
import { applyClass, concatClasses, safeCss, type ClassName } from '@frontend/web-utils';
import type { ButtonProps } from 'react-html-props';

const getHoverEffectClassName = ({ hoverEffect }: PickRequired<LinkButtonProps, 'hoverEffect'>) =>
  match(hoverEffect)
    .with('background', () => safeCss('hover-background-effect'))
    .with('opacity', () => safeCss('hover-opacity-effect'))
    .exhaustive();

export type ActionButtonProps = StrictOmit<TextProps, 'children' | 'className' | 'classNames' | 'color' | 'onClick'> & {
  className?: ClassName;
  color?: StrictExtract<TextProps['color'], 'danger' | 'primary' | 'secondary' | 'success' | 'tertiary' | 'warning'>;
  disabled?: boolean;
  hoverEffect?: 'background' | 'opacity';
  iconColor?: IconProps['color'];
  isLoading?: boolean;
  leftIconName?: IconName;
  leftIconProps?: StrictOmit<IconProps, 'name'>;
  onClick: ButtonProps['onClick'];
  textClassName?: ClassName;
  variant?: 'filled' | 'outline';
} & ({ children: React.ReactNode } | { label: string });

export const ActionButton = ({
  className,
  color = 'primary',
  disabled = false,
  iconColor = color,
  isLoading,
  leftIconName,
  leftIconProps,
  onClick,
  textClassName,
  variant,
  hoverEffect = 'opacity',
  ...props
}: ActionButtonProps) => (
  <button
    className={concatClasses(
      'flex flex-row items-center gap-x-xs justify-center border-borderSecondary border-1 rounded-md p-xs pr-sm',
      getHoverEffectClassName({ hoverEffect }),
      className,
      applyClass(variant === 'filled', 'bg-surfaceAccent', 'bg-transparent'),
    )}
    disabled={disabled}
    onClick={
      disabled
        ? undefined
        : (e) => {
            e.stopPropagation();
            e.preventDefault();
            onClick?.(e);
          }
    }
  >
    {isLoading ? (
      <Spinner size="xs" />
    ) : (
      <>
        {leftIconName ? <Icon color={iconColor} name={leftIconName} size="md" {...leftIconProps} /> : null}
        <Text
          children={'children' in props ? props.children : props.label}
          className={textClassName}
          color={color}
          type="h6"
          {...props}
        />
      </>
    )}
  </button>
);
